import { useState } from 'react';
import { Button, Stack, Text, TextInput } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconMovie, IconUpload, IconX } from '@tabler/icons-react';
import type { AdminVideo, VideoPurpose } from '@/api/types';
import { formatBytes } from '@/lib/format';
import { useUploads } from '@/features/uploads/context';

const VIDEO_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/x-matroska',
  'video/webm',
  'video/x-msvideo',
  'video/mpeg',
];

/** Pick a file and start a tus upload. The upload continues in the background (header "Uploads"). */
export function UploadVideoForm({
  purpose,
  defaultTitle,
  onCreated,
}: {
  purpose: VideoPurpose;
  defaultTitle?: string;
  onCreated?: (v: AdminVideo) => void | Promise<void>;
}) {
  const { start } = useUploads();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState(defaultTitle ?? '');
  const [busy, setBusy] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      await start({ file, title: title.trim() || file.name, purpose, onCreated });
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="sm">
      <Dropzone
        onDrop={(files) => {
          const f = files[0] ?? null;
          setFile(f);
          setRejected(null);
          if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, ''));
        }}
        onReject={() => setRejected('That file isn’t a supported video (MP4, MOV, MKV, WebM).')}
        accept={VIDEO_TYPES}
        multiple={false}
        aria-label="Video file"
      >
        <Stack align="center" gap={4} py="md" style={{ pointerEvents: 'none' }}>
          <Dropzone.Accept>
            <IconUpload size={28} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={28} />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconMovie size={28} />
          </Dropzone.Idle>
          <Text size="sm" fw={500}>
            {file
              ? `${file.name} (${formatBytes(file.size)})`
              : 'Drop a video file here, or click to choose'}
          </Text>
          <Text size="xs" c="dimmed">
            MP4 / MOV / MKV / WebM. Large files are fine — uploads can be paused and resumed.
          </Text>
        </Stack>
      </Dropzone>
      {rejected && (
        <Text size="sm" c="coral.8" role="alert">
          {rejected}
        </Text>
      )}
      <TextInput
        label="Video title"
        value={title}
        onChange={(e) => setTitle(e.currentTarget.value)}
      />
      <Button
        onClick={() => void submit()}
        disabled={!file}
        loading={busy}
        leftSection={<IconUpload size={16} />}
      >
        Start upload
      </Button>
    </Stack>
  );
}
